/**
 * API Clients service — admin-only.
 *
 * Wraps /api/v1/admin/api-clients/* for the AdminDashboard "API Clients" tab.
 *
 * Auth note: uses the shared axios instance from `../shared/services/api`, which
 * attaches the Bearer token automatically via its request interceptor (reads from
 * the Zustand authStore). No manual token handling needed here.
 */
import api from '../shared/services/api';

export type ApiClientScope =
  | 'tariff:classify-direct'
  | 'tariff:classify'
  | 'tariff:classify-file'
  | 'tariff:classify-document'
  | 'tariff:classify-bulk'
  | 'tariff:shipping'
  | 'tariff:feedback';

export interface ApiClientListItem {
  id: string;
  name: string;
  client_id: string;
  scopes: ApiClientScope[];
  is_active: boolean;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ApiClientCreatedSecret {
  id: string;
  name: string;
  client_id: string;
  client_secret: string; // plaintext — surface this in the one-time modal
  scopes: ApiClientScope[];
  is_active: boolean;
  created_at: string;
}

export interface ApiClientRotatedSecret {
  id: string;
  client_id: string;
  client_secret: string;
}

export async function listApiClients(): Promise<ApiClientListItem[]> {
  const r = await api.get<ApiClientListItem[]>('/api/v1/admin/api-clients');
  return r.data;
}

export async function createApiClient(
  name: string,
  scopes: ApiClientScope[],
): Promise<ApiClientCreatedSecret> {
  const r = await api.post<ApiClientCreatedSecret>('/api/v1/admin/api-clients', {
    name,
    scopes,
  });
  return r.data;
}

export async function revokeApiClient(id: string): Promise<void> {
  await api.patch(`/api/v1/admin/api-clients/${id}/revoke`, {});
}

export async function rotateApiClientSecret(id: string): Promise<ApiClientRotatedSecret> {
  const r = await api.post<ApiClientRotatedSecret>(
    `/api/v1/admin/api-clients/${id}/rotate-secret`,
    {},
  );
  return r.data;
}

/**
 * Replace the client's scope list.
 *
 * Already-issued tokens keep their old scopes until expiry (≤60 min); the new
 * scopes take effect on the partner's next token mint. Returns the updated
 * client (no secret in the response — same shape as the list view).
 */
export async function updateApiClientScopes(
  id: string,
  scopes: ApiClientScope[],
): Promise<ApiClientListItem> {
  const r = await api.patch<ApiClientListItem>(
    `/api/v1/admin/api-clients/${id}/scopes`,
    { scopes },
  );
  return r.data;
}

export interface ApiClientPatch {
  name?: string;
  scopes?: ApiClientScope[];
}

/**
 * Generic edit endpoint — update any subset of {name, scopes}. At least one
 * field must be provided. Same token semantics as updateApiClientScopes for
 * scope changes (next-mint application). Returns the updated client.
 */
export async function updateApiClient(
  id: string,
  patch: ApiClientPatch,
): Promise<ApiClientListItem> {
  const r = await api.patch<ApiClientListItem>(
    `/api/v1/admin/api-clients/${id}`,
    patch,
  );
  return r.data;
}

export interface ApiScopeLeaf {
  scope: ApiClientScope;
  label: string;
}

export interface ApiScopeGroup {
  app: string;
  label: string;
  children: ApiScopeLeaf[];
}

export async function listApiScopes(): Promise<ApiScopeGroup[]> {
  const r = await api.get<ApiScopeGroup[]>('/api/v1/admin/api-scopes');
  return r.data;
}
